import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth";
import { LayoutDashboard, ClipboardList, Plus, Users, LogOut, Menu, X, Tag, PackageOpen, LayoutGrid, MapPin, Palette } from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_brand-analytics-10/artifacts/1ogynudb_logo_horizontal_HT.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_brand-analytics-10/artifacts/mmekj5gq_logo_tzanetatos_.png";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const inBandeo = location.pathname.startsWith("/bandeos") || location.pathname === "/admin/map";
  const inMaquillaje = location.pathname.startsWith("/maquillajes");
  const moduleLabel = inMaquillaje ? "MAQUILLAJE" : inBandeo ? "BANDEO" : "TRADE";

  const tradeNav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/reports", label: "Reportes", icon: ClipboardList },
    { to: "/reports/new", label: "Nuevo reporte", icon: Plus, highlight: true },
  ];
  const bandeoNav = [
    { to: "/bandeos", label: "Bandeos", icon: ClipboardList },
    { to: "/bandeos/new", label: "Nuevo bandeo", icon: Plus, highlight: true },
  ];
  const maquillajeNav = [
    { to: "/maquillajes", label: "Diagnósticos", icon: ClipboardList },
    { to: "/maquillajes/new", label: "Nuevo diagnóstico", icon: Plus, highlight: true },
  ];

  const nav = inMaquillaje ? maquillajeNav : inBandeo ? bandeoNav : tradeNav;
  const adminNav = [];
  if (user?.role === "admin") {
    adminNav.push({ to: "/admin/users", label: "Usuarios", icon: Users });
    adminNav.push({ to: "/admin/brands", label: "Marcas", icon: Tag });
    if (inBandeo) adminNav.push({ to: "/admin/map", label: "Mapa de visitas", icon: MapPin });
  }

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const sidebarContent = (
    <div className="h-full flex flex-col bg-white border-r border-slate-200">
      <div className="px-6 py-6 border-b border-slate-200 bg-slate-950">
        <img src={LOGO_HORIZONTAL} alt="Tzanetatos" className="h-10 object-contain" />
        <div className="kbd-label !text-[9px] !text-emerald-400/80 mt-2">Módulo · {moduleLabel}</div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <button
          onClick={() => { navigate("/modules"); setMobileOpen(false); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md border border-emerald-200 bg-emerald-50/50 text-emerald-800 hover:bg-emerald-50 mb-3"
          data-testid="nav-modules"
        >
          {inMaquillaje ? <Palette className="w-4 h-4" /> : inBandeo ? <PackageOpen className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
          Cambiar módulo
        </button>

        <div className="kbd-label px-3 py-2">/ Navegación</div>
        {nav.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/bandeos" || item.to === "/reports" || item.to === "/dashboard" || item.to === "/maquillajes"}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors rounded-md ${
                  isActive
                    ? "bg-emerald-700 text-white"
                    : item.highlight
                    ? "text-emerald-800 hover:bg-emerald-50 border border-emerald-200 bg-emerald-50/50"
                    : "text-slate-700 hover:bg-slate-100"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <Icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          );
        })}

        {adminNav.length > 0 && (
          <>
            <div className="kbd-label px-3 py-2 mt-4">/ Administración</div>
            {adminNav.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors rounded-md ${
                      isActive ? "bg-emerald-700 text-white" : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </>
        )}
      </nav>

      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 px-2 py-2 mb-2">
          <div className="w-9 h-9 bg-emerald-700 text-white font-display font-bold flex items-center justify-center rounded-full text-sm">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
            <div className="kbd-label !text-[9px] truncate">
              {user?.position || (user?.role === "admin" ? "Administrador" : "Usuario")}
            </div>
          </div>
        </div>
        <Button
          onClick={onLogout}
          variant="outline"
          className="w-full justify-start gap-2 text-slate-700 border-slate-200 hover:bg-slate-100 rounded-md"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Cerrar sesión
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:block w-64 shrink-0 sticky top-0 h-screen">
        {sidebarContent}
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-slate-950/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 h-full">
            {sidebarContent}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200 px-4 h-14 flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} className="p-2 hover:bg-slate-100 rounded-md" data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO_ICON} alt="Tzanetatos" className="h-8 w-8 object-contain" />
            <span className="kbd-label !text-[10px]">{moduleLabel}</span>
          </div>
          <div className="w-9" />
        </header>

        <div key={location.pathname} className="page-fade-in">
          <Outlet />
        </div>
      </main>

      {mobileOpen && (
        <button onClick={() => setMobileOpen(false)} className="lg:hidden fixed top-3 right-3 z-[60] bg-white border border-slate-200 rounded-md p-2 shadow-md">
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
